import $ from 'jquery';
import Analytics from '../analytics';
import Environment from '../environment';
import Eventable from '../eventable';
import { getLogger } from '../logging/logger';

const CN = '[MoneyBadger]';
const logger = getLogger('MoneyBadger');

const PROTOCOL = 'xre://';
const PATH = '/guide/x2/entity_info';
const QUERY_PARAMS = '?entityType=MOVIE&forceChangeState=true&entityId=';

class ComcastMoneyBadger extends Eventable {
	constructor() {
		logger.log('[constructor]');

		super();

		document.addEventListener('click', this.deepLinkHrefListener.bind(this));
		$(document).one('onMoneyBadgerReady', this.onMoneyBadgerReady.bind(this));

		/**
		 * We are loading the money-badger library dynamically because it tries to
		 * create a global variable and that does not play nice with the normal
		 * approach of importing via Webpack. While we could add the script tag into
		 * our index.html template, dynamically adding it here will only add it if
		 * the MoneyBadger library is needed.
		 */
		const script = document.createElement('script');
		script.async = true;
		script.src = `https://cdn.thor.comcast.com/sdk/money-badger.min.js`;
		document.head.appendChild(script);
	}

	onMoneyBadgerReady() {
		logger.log('[onMoneyBadgerReady]');

		this.trigger('ready');
		window.$badger.launchCompletedMetricsHandler();
	}

	badgerDeepLink(url) {
		logger.log('[badgerDeepLink]', { url });

		if (!Environment.isComcastSetTopBox()) {
			logger.warn('Deep links only work on SetTopBox');
			return;
		}

		if (!Environment.isInHub()) {
			Analytics.sendEvent('MoneyBadgerDeeplink', { url, status: 'pending' });

			window.$badger
				.deeplink(url)
				.success(() => {
					Analytics.sendEvent('MoneyBadgerDeeplink', { status: 'success', url });
				})
				.failure((error) => {
					logger.error('[Deep link failed]', error);
					Analytics.sendEvent('MoneyBadgerDeeplink-ERROR', {
						status: 'fail',
						url,
						error: JSON.stringify({ error }),
					});
				});
		} else {
			// Send deeplink up to the hub, which will trigger it
			let { origin } = window.location;
			if (Environment.isLocal()) {
				// Grab the port from referrer
				const [match, referrerOrigin] = /(https?:\/\/localhost:\d+)/.exec(
					new URL(document.referrer).origin,
				);
				origin = referrerOrigin;
			}
			window.parent.postMessage(url, origin);
		}
	}

	deepLinkHrefListener(event) {
		logger.log('[deepLinkHrefListener]', event);

		if (event.target.nodeName === 'A' && event.target.href.indexOf(PROTOCOL) >= 0) {
			event.preventDefault();
			this.badgerDeepLink(event.target.href);
		}
	}

	deepLink(id) {
		logger.log('[deepLink]', { id });

		if (!id) {
			logger.warn(`Invalid id for deep link: "${id}"`);
			return Promise.reject();
		}

		if (this.isActive()) {
			Analytics.sendEvent('MoneyBadgerDeeplink', { status: 'pending', id });
		} else {
			Analytics.sendEvent('MoneyBadgerDeeplink-Error', {
				status: 'fail',
				id,
				error: '$badger inActive',
			});

			return Promise.reject(new Error('$badger inActive'));
		}

		const link = this.idToUri(id);
		return new Promise((resolve, reject) => {
			window.$badger
				.deeplink(link)
				.success(() => {
					logger.log('Deep link complete!', id);
					Analytics.sendEvent('MoneyBadgerDeeplink', { status: 'success', link });
					resolve();
				})
				.failure((error) => {
					logger.error('Deep link failed', error);
					Analytics.sendEvent('MoneyBadgerDeeplink-Error', {
						status: 'fail',
						link,
						error: JSON.stringify({ error }),
					});
					reject(error);
				});
		});
	}

	idToUri(id) {
		logger.log('[idToUri]', id);
		return PROTOCOL + PATH + QUERY_PARAMS + id;
	}

	getUserInfo() {
		logger.log('[getUserInfo]');

		if (!window.$badger.active()) {
			Analytics.sendEvent('MoneyBadgerGetUserInfo-Error', { error: '$badger inactive' });
			return Promise.reject(new Error(`${CN}[$badger inactive]`));
		}

		return new Promise((resolve, reject) => {
			window.$badger
				.info()
				.success((info) => {
					// boxUTCoffset is available to determine the real
					// time according to the timezone customer is in
					this.boxUTCoffset = info.timezone || '';
					logger.info('[$badger info]', { info });

					resolve(info);
				})
				.failure((error) => {
					logger.error('[$badger info]', { error });
					Analytics.sendEvent('MoneyBadgerGetUserInfo-Error', {
						status: 'fail',
						error: JSON.stringify({ error }),
					});
					reject(error);
				});
		});
	}

	promptEmailSignUp(headerText, selectNoneText) {
		logger.log('[promptEmailSignUp]', headerText, selectNoneText);

		return new Promise((resolve, reject) => {
			if (!window.$badger.active()) {
				reject();
				return;
			}

			window.$badger
				.userPreferences()
				.promptSignUpEmail({
					headerText: headerText,
					selectNoneText: selectNoneText,
				})
				.success((response) => {
					if (response.status === 'SUCCESS') {
						resolve(response.data.email);
					} else {
						logger.error('[PromptEmailSignUp Error]', { response });
						reject(response);
					}
				})
				.failure(reject);
		});
	}

	promptEmailSignIn(headerText, selectNoneText) {
		logger.log('[promptEmailSignIn]', headerText, selectNoneText);

		window.$badger.userPreferences().promptSignInEmail({
			headerText: headerText,
			selectNoneText: selectNoneText,
		});
	}

	getAppAuth() {
		logger.log('[getAppAuth]');

		return new Promise((resolve, reject) => {
			window.$badger.getAppAuth().get().success(resolve).failure(reject);
		});
	}

	upsertAppAuth(userId, data) {
		logger.log('[upsertAppAuth]', userId, data);

		return new Promise((resolve, reject) => {
			this.getAppAuth().upsert(userId, data).success(resolve).failure(reject);
		});
	}

	deleteAppAuth() {
		logger.log('[deleteAppAuth]');
		logger.warn('Disableing $badger.deleteAppAuth until further testing');
		// this.getAppAuth().delete().success(d.resole).failure(d.resolve);
		return Promise.reject(
			new Error('Disabled access to $badger.deleteAppAuth until further testing'),
		);
	}

	getBearerToken(clientId) {
		logger.log('[getBearerToken]');

		return new Promise((resolve, reject) => {
			window.$badger.getBearerToken(clientId).success(resolve).failure(reject);
		});
	}

	// displayError will always exit after user presses a button
	displayError(title, body) {
		logger.log('[displayError]', title, body);
		window.$badger.displayError(title, body);
	}

	shutdown() {
		logger.log('[shutdown]');
		window.$badger.shutdown();
	}

	isActive() {
		logger.log('[isActive]');
		const isActive = window.$badger.active();
		if (!isActive) {
			logger.error('[$badger inactive]');
		}
		return isActive;
	}

	/**
	 * If MoneyBadger fails to initialize, it will do so silently. This method will return
	 * the service object to be used which indicates if MoneyBadger is supported or not by this device.
	 */
	getMoneyBadgerService() {
		logger.log('[getMoneyBadgerService]');
		return new Promise((resolve) => {
			const testService = (service) => {
				if (service) {
					resolve(!!service);
				} else {
					const msg = '[MISSING "com.comcast.BridgeObject_1" service]';
					logger.error(msg);
					resolve(undefined);
				}
			};

			/* Find the Bridge Object */
			if (typeof ServiceManager !== 'undefined' && window.ServiceManager != null) {
				if ('version' in window.ServiceManager) {
					/** WPE */
					logger.log('[Detected WPE browser]');
					window.ServiceManager.getServiceForJavaScript(
						'com.comcast.BridgeObject_1',
						testService,
					);
				} else {
					/** QT */
					logger.log('[Detected QT browser]');
					const createService =
						window.ServiceManager.createService ||
						window.ServiceManager.getServiceForJavaScript;
					testService(createService('com.comcast.BridgeObject_1'));
				}
			} else {
				const msg = `${CN}[Failed to find ServiceManager]`;
				logger.error(msg);
				resolve();
			}
		});
	}
}

export default new ComcastMoneyBadger();
